/**
 * Route → permission map + the permission catalog (S0.2 extraction, S10.1
 * made data-driven).
 *
 * TWO responsibilities, one source of truth:
 *  1. `ROUTE_PERMISSIONS` / `getRequiredPermission()` — which permission string
 *     gates each /auth route prefix. `ProtectedRoute.tsx` consumes this
 *     (its old inline table is gone; S0.2 followUp closed).
 *  2. `PERMISSION_CATALOG` — the full grouped catalog of permission keys. The
 *     S10.1 role-permission matrix (系統 › 角色) renders from it, the seeded
 *     role presets grant subsets of it, and a user's effective `permissions[]`
 *     is the union of their assigned roles' grants. So the route guard and the
 *     governance matrix read the SAME permission vocabulary — the guard is now
 *     data-driven from the matrix rather than a hand-kept parallel list.
 *
 * Keep in sync with the routes declared in `src/App.tsx` and the nav in
 * `navigation.constants.ts` (module-checklist touchpoint).
 */

export interface RoutePermissionRule {
  /** Route prefix under /auth (matches the exact path or any sub-path). */
  prefix: string;
  /** Permission string required (admin role bypasses in ProtectedRoute). */
  permission: string;
}

/**
 * Grouped permission vocabulary. `moduleKey` groups columns in the role matrix
 * and maps to an i18n label under `governance.permissionModules.*`; each
 * permission key maps to `governance.permissions.*`.
 */
export interface PermissionModuleDef {
  moduleKey: string;
  permissions: readonly string[];
}

export const PERMISSION_CATALOG: readonly PermissionModuleDef[] = [
  { moduleKey: 'masterData', permissions: ['vendors.view', 'customers.view', 'products.manage', 'categories.manage'] },
  { moduleKey: 'sales', permissions: ['sales_orders.view', 'sales_orders.manage', 'claims.manage'] },
  { moduleKey: 'procurement', permissions: ['purchase_orders.manage'] },
  { moduleKey: 'occ', permissions: ['occ.view'] },
  { moduleKey: 'shipping', permissions: ['shipping.manage', 'receipts.manage'] },
  { moduleKey: 'finance', permissions: ['finance.view', 'payments.manage'] },
  { moduleKey: 'reports', permissions: ['reports.view'] },
  { moduleKey: 'system', permissions: ['governance.manage'] },
] as const;

/** Flat list of every catalogued permission key. */
export const ALL_PERMISSIONS: readonly string[] = PERMISSION_CATALOG.flatMap(
  (module) => module.permissions
);

export const ROUTE_PERMISSIONS: readonly RoutePermissionRule[] = [
  // S4.2 wiring check — EVERY rbacV2 governance surface is a route-derived tab
  // under this ONE prefix (/auth/governance/:tab, tab = organisations | roles |
  // hierarchy | users | audit), so the existing `governance.manage` rule below
  // already gates all of them (organisations tree S2.1, matrix S2.2, hierarchy
  // + levels S2.3, scoped assignment S2.4, preview S3.1, impact S3.3,
  // lifecycle S3.4). The System-module nav deep links added in
  // `navigation.constants.ts` point INTO these tabs. NO new route-gating key
  // was added by rbacV2 — the catalog keys above this line stay frozen, and
  // the route guard remains a pure Axis-A `can()` check (docs/rbac.md).
  { prefix: '/auth/governance', permission: 'governance.manage' },
  // S7.2 — the SCN Spec Master (/auth/products/scn[/:scn]) deliberately rides
  // THIS prefix rule: Frame 274「SCN 要開權限」ships on the existing
  // `products.manage` build-default (Q-O1 — a dedicated SCN permission would
  // be a new catalog key; the catalog above is frozen). No new row needed —
  // the prefix match covers the sub-paths (pinned by scnRoutes.test.ts).
  { prefix: '/auth/products', permission: 'products.manage' },
  { prefix: '/auth/product-taxonomy', permission: 'products.manage' },
  { prefix: '/auth/sales-orders', permission: 'sales_orders.view' },
  { prefix: '/auth/claims', permission: 'claims.manage' },
  { prefix: '/auth/purchase-orders', permission: 'purchase_orders.manage' },
  { prefix: '/auth/occ', permission: 'occ.view' },
  { prefix: '/auth/shipments', permission: 'shipping.manage' },
  { prefix: '/auth/shipping-notices', permission: 'shipping.manage' },
  { prefix: '/auth/goods-receipts', permission: 'receipts.manage' },
  { prefix: '/auth/finance', permission: 'finance.view' },
  { prefix: '/auth/engines', permission: 'finance.view' },
  { prefix: '/auth/pricing', permission: 'finance.view' },
  { prefix: '/auth/reports', permission: 'reports.view' },
];

/** Permission required for a pathname, or undefined when the route is open. */
export function getRequiredPermission(pathname: string): string | undefined {
  return ROUTE_PERMISSIONS.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )?.permission;
}

/* ------------------------------------------------------------------------- *
 * S0.2 — CRUD-type metadata + implication catalog (rbacV2, additive)
 *
 * Everything ABOVE this line is FROZEN and byte-compatible: `PERMISSION_CATALOG`,
 * `ALL_PERMISSIONS`, `ROUTE_PERMISSIONS`, and `getRequiredPermission()` are the
 * route-gating spine (consumed by `ProtectedRoute`, `RolesTab`, and the seed
 * presets) and must never change shape. This section only ADDS the fleet-style
 * CRUD-type layer the rbacV2 matrix (S2.2) and the resolver (S0.4) read.
 * ------------------------------------------------------------------------- */

/**
 * CRUD-shaped permission types (grows the flat catalog toward fleet's
 * `constants/permissionHierarchy.ts` shape without touching route gating). The
 * seven members are the fixed badge vocabulary — `view` renders as the "Read"
 * (R) badge, `edit` as "Update" (U); `business`/`system` are forward-looking
 * coarse-grant types with no current catalogued member.
 */
export type PermissionType =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'manage'
  | 'business'
  | 'system';

/** Every `PermissionType` in badge order (C · R · U · D · M · B · S). */
export const PERMISSION_TYPES: readonly PermissionType[] = [
  'create',
  'view',
  'edit',
  'delete',
  'manage',
  'business',
  'system',
] as const;

/**
 * The dedicated cost-basis-edit permission (Open Q3 = a DEDICATED permission,
 * NOT a reuse of `finance.view`). Seeded (S0.5) onto every role that edits cost
 * basis today, then read by `usePricing` (S1.3). It is an EXTENDED permission:
 * deliberately kept OUT of `PERMISSION_CATALOG`/`ALL_PERMISSIONS` so the flat
 * union (and thus admin's `[...ALL_PERMISSIONS]` seed and every route key) stays
 * byte-compatible — it only exists for the rbacV2 resolver and the seed.
 */
export const FINANCE_COST_BASIS_EDIT = 'finance.costBasis.edit';

/**
 * Permissions that exist for the rbacV2 resolver/seed but are NOT part of the
 * flat `ALL_PERMISSIONS` union (so ALL_PERMISSIONS stays unchanged with the flag
 * OFF). Kept as a named list so the seed and matrix can enumerate them.
 */
export const EXTENDED_PERMISSIONS: readonly string[] = [FINANCE_COST_BASIS_EDIT];

/**
 * Per-code CRUD type — the metadata layer over the frozen catalog. Covers every
 * `ALL_PERMISSIONS` code plus the extended `finance.costBasis.edit`. Implied /
 * phantom codes not listed here (e.g. `products.view` produced by the implication
 * table) fall through to suffix inference in `getPermissionType()`.
 */
export const PERMISSION_TYPE_MAP: Readonly<Record<string, PermissionType>> = {
  'vendors.view': 'view',
  'customers.view': 'view',
  'products.manage': 'manage',
  'categories.manage': 'manage',
  'sales_orders.view': 'view',
  'sales_orders.manage': 'manage',
  'claims.manage': 'manage',
  'purchase_orders.manage': 'manage',
  'occ.view': 'view',
  'shipping.manage': 'manage',
  'receipts.manage': 'manage',
  'finance.view': 'view',
  'payments.manage': 'manage',
  'reports.view': 'view',
  'governance.manage': 'manage',
  [FINANCE_COST_BASIS_EDIT]: 'edit',
};

/**
 * AntD theme-token KEYS the badge colour resolves from at render time
 * (`token[colorToken]`). Never a raw hex — house rule. `colorInfo` resolves to
 * the brand/info hue; the neutral text tokens carry the passive read/system
 * badges.
 */
export type PermissionBadgeColorToken =
  | 'colorSuccess'
  | 'colorTextSecondary'
  | 'colorWarning'
  | 'colorError'
  | 'colorPrimary'
  | 'colorInfo'
  | 'colorTextTertiary';

/** One-letter CRUD badge glyph. */
export type PermissionTypeGlyph = 'C' | 'R' | 'U' | 'D' | 'M' | 'B' | 'S';

export interface PermissionTypeConfig {
  /**
   * The CRUD badge glyph (C/R/U/D/M/B/S). A SECONDARY, at-a-glance cue only —
   * NEVER the sole signal. The accessible name is the `i18nKey` full word,
   * surfaced as both a tooltip AND an `aria-label`, and the badge colour is a
   * tertiary cue; a bare glyph or colour-alone is not accessible (ux-lens
   * colour-only / bare-glyph rule).
   */
  glyph: PermissionTypeGlyph;
  /** AntD token key the badge colour resolves from at render (never a raw hex). */
  colorToken: PermissionBadgeColorToken;
  /**
   * Fully-qualified i18n key for the full-word accessible name
   * (`governance.permissionTypes.*`) — feeds the badge tooltip + `aria-label`.
   */
  i18nKey: string;
}

/**
 * The single source of truth for CRUD badge glyph + colour + accessible name
 * (mirrors fleet's `PERMISSION_TYPE_CONFIG`, but colours are token KEYS not raw
 * hex, and the full word comes from i18n not a hardcoded `full`). Consumed by
 * the rbacV2 matrix (S2.2). Each entry MUST expose a full-word name via
 * `i18nKey` so no badge is ever a bare glyph.
 */
export const PERMISSION_TYPE_CONFIG: Readonly<
  Record<PermissionType, PermissionTypeConfig>
> = {
  create: {
    glyph: 'C',
    colorToken: 'colorSuccess',
    i18nKey: 'governance:permissionTypes.create',
  },
  view: {
    glyph: 'R',
    colorToken: 'colorTextSecondary',
    i18nKey: 'governance:permissionTypes.view',
  },
  edit: {
    glyph: 'U',
    colorToken: 'colorWarning',
    i18nKey: 'governance:permissionTypes.edit',
  },
  delete: {
    glyph: 'D',
    colorToken: 'colorError',
    i18nKey: 'governance:permissionTypes.delete',
  },
  manage: {
    glyph: 'M',
    colorToken: 'colorPrimary',
    i18nKey: 'governance:permissionTypes.manage',
  },
  business: {
    glyph: 'B',
    colorToken: 'colorInfo',
    i18nKey: 'governance:permissionTypes.business',
  },
  system: {
    glyph: 'S',
    colorToken: 'colorTextTertiary',
    i18nKey: 'governance:permissionTypes.system',
  },
};

/**
 * The CRUD type of any permission code. Explicit `PERMISSION_TYPE_MAP` wins;
 * otherwise the final dotted segment is inferred (so implication-produced phantom
 * codes like `products.view` still badge correctly); unknown ⇒ `view`.
 */
export function getPermissionType(code: string): PermissionType {
  const explicit = PERMISSION_TYPE_MAP[code];
  if (explicit) return explicit;
  const suffix = code.slice(code.lastIndexOf('.') + 1);
  return (PERMISSION_TYPES as readonly string[]).includes(suffix)
    ? (suffix as PermissionType)
    : 'view';
}
